import { useEffect, useState } from 'react';

/** Where the visible part of the page sits in the layout viewport, in CSS px. */
interface ViewportBox {
    top: number;
    height: number;
}

// Differences smaller than this are rounding, not a keyboard.
const VIEWPORT_EPSILON_PX = 1;
const SCALE_EPSILON = 0.01;

/**
 * The part of the page the viewer can see, while it is not the whole layout
 * viewport: an on-screen keyboard is up. Null otherwise.
 *
 * iOS opens the keyboard over the page without resizing it and pans what is
 * visible up to the focused field, so a shell sized to the layout viewport has
 * its bottom under the keyboard and its top panned off screen - and a composer
 * padded by the keyboard's height on top of that pan lands near the top of the
 * screen. A shell pinned to this box keeps both edges in view instead, with a
 * composer at its bottom resting on the keyboard. Android resizes the layout
 * viewport itself (index.html's `interactive-widget`), which leaves this null.
 *
 * A pinch-zoomed page is not followed: the viewer zoomed in to see less of it.
 */
export function useVisualViewportBox(): ViewportBox | null {
    const [box, setBox] = useState<ViewportBox | null>(null);

    useEffect(() => {
        const viewport = window.visualViewport;
        if (!viewport) {
            return undefined;
        }
        const update = (): void => {
            const isWhole = viewport.offsetTop < VIEWPORT_EPSILON_PX &&
                window.innerHeight - viewport.height < VIEWPORT_EPSILON_PX;
            const isZoomed = Math.abs(viewport.scale - 1) > SCALE_EPSILON;
            const nextValue = isWhole || isZoomed
                ? null
                : { top: Math.round(viewport.offsetTop), height: Math.round(viewport.height) };
            setBox((previous) => (
                previous?.top === nextValue?.top &&
                previous?.height === nextValue?.height ? previous : nextValue
            ));
        };
        viewport.addEventListener('resize', update);
        viewport.addEventListener('scroll', update);
        update();
        return () => {
            viewport.removeEventListener('resize', update);
            viewport.removeEventListener('scroll', update);
        };
    }, []);

    return box;
}
